/**
 * DATA-17: Segment Tab — User behavior segmentation distribution.
 * Pie chart (CSS circles), segment counts + percentages, drill-down to user list.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchSegmentDistribution,
  fetchSegmentUsers,
} from "../../api/admin-segmentation";
import type {
  SegmentDistributionRow,
  SegmentUserRow,
} from "../../api/admin-segmentation";
import { Td, Th, tableStyles } from "../../components/DataTable";
import { t } from "../../i18n";
import * as shared from "../../styles/shared";
import { defaultDateRange, fmt } from "./helpers";

// ─── Segment Colors ───

const SEGMENT_COLORS: Record<string, string> = {
  power_user: "#7c3aed",
  builder: "#2563eb",
  organizer: "#059669",
  reader: "#d97706",
  chat_only: "#6b7280",
};

const SEGMENT_LABELS: Record<string, string> = {
  power_user: "segment.power_user",
  builder: "segment.builder",
  organizer: "segment.organizer",
  reader: "segment.reader",
  chat_only: "segment.chat_only",
};

export function SegmentTab(): React.JSX.Element {
  const [date, setDate] = useState(defaultDateRange(1).end);
  const { data, error, loading, load } = useSegmentData();
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    void load(date);
  }, [load, date]);

  return (
    <div>
      <DatePicker date={date} onChange={setDate} loading={loading} />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && (
        <p style={shared.loadingText}>{t("common.loading")}</p>
      )}
      {data && data.length > 0 && (
        <DistributionView
          rows={data}
          onSelect={setSelected}
          selected={selected}
        />
      )}
      {data && data.length === 0 && (
        <p style={shared.loadingText}>{t("segment.no_data")}</p>
      )}
      {selected && <UserDrillDown date={date} segment={selected} />}
    </div>
  );
}

// ─── Distribution View ───

function DistributionView({
  rows,
  onSelect,
  selected,
}: {
  rows: ReadonlyArray<SegmentDistributionRow>;
  onSelect: (seg: string) => void;
  selected: string | null;
}): React.JSX.Element {
  const total = rows.reduce((sum, r) => sum + r.user_count, 0);

  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>{t("segment.distribution")}</h3>
      <div style={s.distGrid}>
        <PieChart rows={rows} total={total} />
        <SegmentCards
          rows={rows}
          total={total}
          onSelect={onSelect}
          selected={selected}
        />
      </div>
    </section>
  );
}

// ─── CSS Pie Chart ───

function PieChart({
  rows,
  total,
}: {
  rows: ReadonlyArray<SegmentDistributionRow>;
  total: number;
}): React.JSX.Element {
  const gradient = buildConicGradient(rows, total);

  return (
    <div style={s.pieWrap}>
      <div style={{ ...s.pie, background: gradient }} />
      <span style={s.pieCenter}>{fmt(total)}</span>
    </div>
  );
}

function buildConicGradient(
  rows: ReadonlyArray<SegmentDistributionRow>,
  total: number,
): string {
  if (total === 0) return "conic-gradient(#e5e7eb 0deg 360deg)";
  const stops: string[] = [];
  let offset = 0;
  for (const r of rows) {
    const pct = (r.user_count / total) * 360;
    const color = SEGMENT_COLORS[r.segment] ?? "#6b7280";
    stops.push(`${color} ${offset}deg ${offset + pct}deg`);
    offset += pct;
  }
  return `conic-gradient(${stops.join(", ")})`;
}

// ─── Segment Cards ───

function SegmentCards({
  rows,
  total,
  onSelect,
  selected,
}: {
  rows: ReadonlyArray<SegmentDistributionRow>;
  total: number;
  onSelect: (seg: string) => void;
  selected: string | null;
}): React.JSX.Element {
  return (
    <div style={s.cardList}>
      {rows.map((r) => {
        const pct = total > 0 ? ((r.user_count / total) * 100).toFixed(1) : "0";
        const isActive = selected === r.segment;
        return (
          <button
            key={r.segment}
            type="button"
            onClick={() => onSelect(r.segment)}
            style={isActive ? { ...s.segCard, ...s.segCardActive } : s.segCard}
          >
            <span
              style={{
                ...s.dot,
                background: SEGMENT_COLORS[r.segment] ?? "#6b7280",
              }}
            />
            <span style={s.segLabel}>
              {t(SEGMENT_LABELS[r.segment] ?? r.segment)}
            </span>
            <span style={s.segCount}>
              {fmt(r.user_count)} ({pct}%)
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── User Drill-Down ───

function UserDrillDown({
  date,
  segment,
}: { date: string; segment: string }): React.JSX.Element {
  const { users, total, error, loading, load } = useUserDrillDown();
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    setPage(0);
    void load(date, segment, limit, 0);
  }, [load, date, segment]);

  useEffect(() => {
    void load(date, segment, limit, page * limit);
  }, [load, date, segment, page]);

  return (
    <section style={shared.section}>
      <h3 style={shared.sectionTitle}>
        {t(SEGMENT_LABELS[segment] ?? segment)} {t("segment.users_title")} (
        {fmt(total)})
      </h3>
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && <p style={shared.loadingText}>{t("common.loading")}</p>}
      <UserTable users={users} />
      <PageNav page={page} total={total} limit={limit} onChange={setPage} />
    </section>
  );
}

function UserTable({
  users,
}: {
  users: ReadonlyArray<SegmentUserRow>;
}): React.JSX.Element {
  return (
    <table style={tableStyles.table}>
      <thead>
        <tr>
          <Th>{t("segment.col_user_id")}</Th>
          <Th>{t("segment.col_messages")}</Th>
          <Th>{t("segment.col_summaries")}</Th>
          <Th>{t("segment.col_deliveries")}</Th>
          <Th>{t("segment.col_shares")}</Th>
          <Th>{t("segment.col_favorited")}</Th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => (
          <tr key={u.user_id}>
            <Td mono>{u.user_id.slice(0, 8)}...</Td>
            <Td mono>{u.scores.messages}</Td>
            <Td mono>{u.scores.summaries}</Td>
            <Td mono>{u.scores.deliveries}</Td>
            <Td mono>{u.scores.shares}</Td>
            <Td mono>{u.scores.delivery_favorited}</Td>
          </tr>
        ))}
        {users.length === 0 && (
          <tr>
            <Td colSpan={6}>{t("segment.no_users")}</Td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function PageNav({
  page,
  total,
  limit,
  onChange,
}: {
  page: number;
  total: number;
  limit: number;
  onChange: (p: number) => void;
}): React.JSX.Element {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return <></>;
  return (
    <div style={s.pageNav}>
      <button
        type="button"
        disabled={page <= 0}
        onClick={() => onChange(page - 1)}
        style={s.pageBtn}
      >
        Prev
      </button>
      <span style={s.pageInfo}>
        {page + 1} / {totalPages}
      </span>
      <button
        type="button"
        disabled={page + 1 >= totalPages}
        onClick={() => onChange(page + 1)}
        style={s.pageBtn}
      >
        Next
      </button>
    </div>
  );
}

// ─── Date Picker ───

function DatePicker({
  date,
  onChange,
  loading,
}: {
  date: string;
  onChange: (v: string) => void;
  loading: boolean;
}): React.JSX.Element {
  return (
    <div style={s.dateRow}>
      <label style={s.dateLabel}>
        Date
        <input
          type="date"
          value={date}
          onChange={(e) => onChange(e.target.value)}
          style={s.dateInput}
        />
      </label>
      {loading && <span style={shared.loadingText}>{t("common.loading")}</span>}
    </div>
  );
}

// ─── Hooks ───

function useSegmentData(): {
  data: ReadonlyArray<SegmentDistributionRow> | null;
  error: string | null;
  loading: boolean;
  load: (date: string) => Promise<void>;
} {
  const [data, setData] =
    useState<ReadonlyArray<SegmentDistributionRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchSegmentDistribution(d));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, error, loading, load };
}

function useUserDrillDown(): {
  users: ReadonlyArray<SegmentUserRow>;
  total: number;
  error: string | null;
  loading: boolean;
  load: (d: string, seg: string, lim: number, off: number) => Promise<void>;
} {
  const [users, setUsers] = useState<ReadonlyArray<SegmentUserRow>>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (d: string, seg: string, lim: number, off: number): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchSegmentUsers({
          date: d,
          segment: seg,
          limit: lim,
          offset: off,
        });
        setUsers(res.items);
        setTotal(res.total);
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : t("common.failed_to_load"),
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { users, total, error, loading, load };
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  distGrid: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    gap: 24,
    alignItems: "start",
  },
  pieWrap: {
    position: "relative",
    width: 180,
    height: 180,
  },
  pie: {
    width: 180,
    height: 180,
    borderRadius: "50%",
  },
  pieCenter: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: 20,
    fontWeight: 700,
    fontFamily: "var(--font-mono)",
    background: "var(--color-bg)",
    borderRadius: "50%",
    width: 80,
    height: 80,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardList: { display: "flex", flexDirection: "column", gap: 8 },
  segCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    background: "var(--color-bg)",
    textAlign: "left",
    cursor: "pointer",
  },
  segCardActive: { borderColor: "var(--color-primary)", borderWidth: 2 },
  dot: { width: 12, height: 12, borderRadius: "50%", flexShrink: 0 },
  segLabel: { flex: 1, fontWeight: 500, fontSize: 14 },
  segCount: {
    fontFamily: "var(--font-mono)",
    fontSize: 13,
    color: "var(--color-text-secondary)",
  },
  dateRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 24,
  },
  dateLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  dateInput: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
  },
  pageNav: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    justifyContent: "center",
  },
  pageBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
  },
  pageInfo: { fontSize: 13, color: "var(--color-text-secondary)" },
};
